"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { trackUserLogin } from "@/lib/analytics-service"
import { appleProvider, auth, googleProvider } from "@/lib/firebase-client"
import { ensureUserProfileDocument } from "@/lib/profile-service"
import { zodResolver } from "@hookform/resolvers/zod"
import { FirebaseError } from "firebase/app"
import { 
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"

const formSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  password: z.string().min(6, {
    message: "Password must be at least 6 characters.",
  }),
})

type LoginFormProps = {
  redirectTo?: string;
};

export function LoginForm({ redirectTo }: LoginFormProps) {
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setErrorMessage(null)
    setIsSubmitting(true)

    try {
      const userCredential = await signInWithEmailAndPassword(auth, values.email, values.password)
      try {
        await ensureUserProfileDocument(userCredential.user)
      } catch (profileError) {
        console.error("Failed to sync profile after email sign-in", profileError)
      }
      trackUserLogin("email")
      navigate(redirectTo || "/home")
    } catch (err) {
      console.error(err);
      if (err instanceof FirebaseError) {
        const message = mapFirebaseAuthError(err.code)
        form.setError("email", { message })
        form.setError("password", { message: " " })
        setErrorMessage(message)
      } else {
        setErrorMessage("Something went wrong. Please try again.")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleGoogleSignIn() {
    setErrorMessage(null)
    setIsSubmitting(true)

    try {
      const credential = await signInWithPopup(auth, googleProvider)
      try {
        await ensureUserProfileDocument(credential.user, {
          displayName: credential.user.displayName ?? undefined,
          avatarURL: credential.user.photoURL ?? undefined,
        })
      } catch (profileError) {
        console.error("Failed to sync profile after Google sign-in", profileError)
      }
      trackUserLogin("google")
      navigate(redirectTo || "/home")
    } catch (err) {
      console.error(err);
      if (err instanceof FirebaseError) {
        // Handle case where account exists with different credential (email/password)
        if (err.code === "auth/account-exists-with-different-credential") {
          const email = err.customData?.email as string | undefined;
          if (email) {
            setErrorMessage(
              "An account with this email already exists. Please sign in with your email and password, then link your Google account in your profile settings."
            );
          } else {
            setErrorMessage(
              "An account with a different sign-in method already exists. Please sign in with your existing method."
            );
          }
        } else {
          const message = mapFirebaseAuthError(err.code);
          setErrorMessage(message);
        }
      } else {
        setErrorMessage("Google sign-in failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleAppleSignIn() {
    setErrorMessage(null)
    setIsSubmitting(true)

    try {
      const credential = await signInWithPopup(auth, appleProvider)
      try {
        // Apple may not provide displayName on first sign-in, it's in the credential
        // Type assertion for additionalUserInfo which may exist but isn't in the type definition
        type CredentialWithAdditionalInfo = typeof credential & {
          additionalUserInfo?: {
            profile?: {
              name?: {
                firstName?: string;
                lastName?: string;
              };
            };
          };
        };
        const credentialWithInfo = credential as CredentialWithAdditionalInfo;
        const displayName = credential.user.displayName ?? 
          (credentialWithInfo.additionalUserInfo?.profile?.name
            ? `${credentialWithInfo.additionalUserInfo.profile.name.firstName ?? ""} ${credentialWithInfo.additionalUserInfo.profile.name.lastName ?? ""}`.trim()
            : undefined);
        
        await ensureUserProfileDocument(credential.user, {
          displayName: displayName,
          avatarURL: credential.user.photoURL ?? undefined,
        })
      } catch (profileError) {
        console.error("Failed to sync profile after Apple sign-in", profileError)
      }
      trackUserLogin("apple")
      navigate(redirectTo || "/home")
    } catch (err) {
      console.error(err);
      if (err instanceof FirebaseError) {
        // Handle case where account exists with different credential (email/password)
        if (err.code === "auth/account-exists-with-different-credential") {
          const email = err.customData?.email as string | undefined;
          if (email) {
            setErrorMessage(
              "An account with this email already exists. Please sign in with your email and password, then link your Apple account in your profile settings."
            );
          } else {
            setErrorMessage(
              "An account with a different sign-in method already exists. Please sign in with your existing method."
            );
          }
        } else {
          const message = mapFirebaseAuthError(err.code);
          setErrorMessage(message);
        }
      } else {
        setErrorMessage("Apple sign-in failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="bg-white shadow-md">
      <CardContent className="p-6">
        <div className="space-y-2">
          <Button type="button" variant="outline" className="w-full" onClick={handleGoogleSignIn} disabled={isSubmitting}>
            <svg className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512"><path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 126 23.4 172.9 61.9l-76.2 76.2c-27.3-26.2-62.7-42.5-100.9-42.5-87 0-157.9 71-157.9 158s70.9 158 157.9 158c99.9 0 137.8-70.2 142.4-107.4H248v-91.1h236.3c4.7 25.4 7.7 54.2 7.7 85.9z"></path></svg>
            Sign in with Google
          </Button>
          <Button type="button" variant="outline" className="w-full bg-black text-white hover:bg-gray-800" onClick={handleAppleSignIn} disabled={isSubmitting}>
            <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            Sign in with Apple
          </Button>
        </div>
        <div className="relative my-4">
          <Separator />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-2 bg-white">
            <span className="text-sm text-muted-foreground">OR CONTINUE WITH</span>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="name@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" autoComplete="current-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {errorMessage ? (
              <p className="text-sm text-destructive" role="alert">{errorMessage}</p>
            ) : null}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}

function mapFirebaseAuthError(code: string): string {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/invalid-email":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return "Invalid email or password."
    case "auth/popup-closed-by-user":
      return "Sign-in was cancelled before completion."
    case "auth/account-exists-with-different-credential":
      return "An account with this email already exists with a different sign-in method."
    case "auth/popup-blocked":
      return "Pop-up was blocked by your browser. Please allow pop-ups for this site."
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again."
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later."
    case "auth/user-disabled":
      return "This account has been disabled. Please contact support."
    case "auth/operation-not-allowed":
      return "Google sign-in is not enabled. Please contact support."
    default:
      return "Authentication failed. Please try again."
  }
}
